/**
 * Poll budgets, intervals, deadlines, and the pure derivations over them.
 *
 * A dependency-free leaf so the entry builders and the pollers can both read it
 * without importing each other: `buildLongTaskFetchEntry` needs the fetch-tracker
 * floor, while the pollers need `longTaskInterval` and `clampPollInterval`.
 */

export const MAX_POLL_COUNT = 30;
export const DEFAULT_POLL_INTERVAL_MS = 15000;
export const LONG_TASK_NETWORK_ERROR_BUDGET = 5;
/** Floor poll interval (ms) for the hidden long-task results-fetch tracker. */
export const FETCH_TRACKER_MIN_POLL_INTERVAL = 30_000;
/**
 * How long the hidden fetch tracker is given to finish, measured from the row's
 * creation. Past it the tracker is marked terminal and the owning card shows its
 * "Fetch results" recovery action.
 *
 * A wall-clock deadline rather than a count of polls, because the intent is "a
 * fetch still running after 20 minutes is not going to finish" — and a count also
 * charges failed status reads and claim retries against the same allowance, which
 * is not what that intent means.
 */
export const FETCH_TRACKER_DEADLINE_MS = 20 * 60_000;

/** True once the tracker row created at `createdAt` has outlived its deadline. */
export function isFetchTrackerExpired(createdAt: string): boolean {
  return (
    Date.now() - new Date(createdAt).getTime() >= FETCH_TRACKER_DEADLINE_MS
  );
}

/**
 * Returns a positive poll interval, falling back to the default for a missing,
 * zero, or negative value. Guards against a backend `pollInterval` of 0, which
 * would otherwise reschedule at 0ms and busy-loop the live poll.
 */
export function clampPollInterval(ms: number | undefined | null): number {
  return typeof ms === "number" && ms > 0 ? ms : DEFAULT_POLL_INTERVAL_MS;
}

/** Age-derived poll interval: 30s for first 5 min, 2 min up to 1 hr, 10 min after. */
export function longTaskInterval(createdAt: string): number {
  const ageMs = Date.now() - new Date(createdAt).getTime();
  if (ageMs < 5 * 60_000) return 30_000;
  if (ageMs < 60 * 60_000) return 120_000;
  return 600_000;
}
